import React, { useContext, useMemo, useRef, useState } from "react";
import { ModalContext, showErrorDialog } from "@SeemplicityDev/shared";
import {
  searchTree,
  type ScopeNode,
} from "../../../../AppView/ScopeManagement/ScopeProvider/ScopeProvider";
import type { TreeNode } from "../../../../Components/Tree/types";
import useLogger from "../../../../Logger/useLogger";
import { resourceTypes } from "../../../FindingsUtils";
import EditScopeLocationModal from "./EditScopeLocationModal";
import { useMoveScope, useMoveScopeGroup } from "./useScopeGuardrailMutations";
import {
  asTreeNode,
  asTreeNodes,
  collisionMessage,
  withoutSubtree,
} from "./scopeTreeUtils";
import type { ScopeNodeType } from "./types";

export interface MoveScopeModalProps {
  nodeId: string;
  nodeName?: string;
  nodeType: ScopeNodeType;
  root?: ScopeNode | null;
  // The node's current parent; the root when it sits at the top level.
  currentParentId?: string | null;
  onMoved: (location: TreeNode) => void;
}

const MOVE_FAILED_MESSAGE: Record<ScopeNodeType, string> = {
  scope: "Couldn't move this scope. Please try again.",
  group: "Couldn't move this scope group. Please try again.",
};

const MoveScopeModal: React.FC<MoveScopeModalProps> = ({
  nodeId,
  nodeName,
  nodeType,
  root,
  currentParentId,
  onMoved,
}) => {
  const { closeModal } = useContext(ModalContext);
  const { logWarning } = useLogger();
  const { moveScope, loading: movingScope } = useMoveScope();
  const { moveScopeGroup, loading: movingGroup } = useMoveScopeGroup();

  const pickerRoot = useMemo(
    () =>
      nodeType === "group" ? withoutSubtree(root, nodeId) : (root ?? null),
    [root, nodeId, nodeType]
  );
  const currentParent = useMemo(
    () => (root && currentParentId ? searchTree(root, currentParentId) : null),
    [root, currentParentId]
  );
  // Unknown parent: no initial pick, so moving to the root still counts as a change.
  const initialLocation = currentParentId ? currentParent : (root ?? null);
  const leavesParentEmpty =
    !!currentParent &&
    currentParent.type !== resourceTypes.ROOT &&
    (currentParent.children?.length ?? 0) === 1 &&
    currentParent.children?.[0]?.id === nodeId;

  const [location, setLocation] = useState<TreeNode | null>(
    asTreeNode(initialLocation)
  );
  const [collision, setCollision] = useState<string>();
  // `loading` flips a render late; this blocks a double-click.
  const inFlight = useRef(false);

  const onLocationChange = (node: TreeNode) => {
    setLocation(node);
    setCollision(undefined);
  };

  const confirm = () => {
    if (!location || inFlight.current) {
      return;
    }
    inFlight.current = true;
    // The root stands for the top level, which the mutation expresses as no parent.
    const targetId =
      location.type === resourceTypes.ROOT ? null : (location.id ?? null);
    const write =
      nodeType === "scope"
        ? moveScope(nodeId, { scopeGroupId: targetId })
        : moveScopeGroup(nodeId, { parentGroupId: targetId });
    write
      .then(({ ok, nameCollisions }) => {
        if (ok) {
          onMoved(location);
          closeModal();
          return;
        }
        setCollision(collisionMessage(nameCollisions, nodeType, nodeName));
      })
      .catch((err: unknown) => {
        // The BE logs the failure; a warning keeps the raw error out of Sentry.
        logWarning(
          err,
          `Scope guardrails: move_${nodeType === "scope" ? "finding_scope" : "scope_group"}_v3 failed`
        );
        showErrorDialog({ message: MOVE_FAILED_MESSAGE[nodeType] });
      })
      .finally(() => {
        inFlight.current = false;
      });
  };

  const unchanged = !location || location.id === initialLocation?.id;

  return (
    <EditScopeLocationModal
      nodeName={nodeName}
      nodeType={nodeType}
      root={pickerRoot ? asTreeNodes([pickerRoot]) : null}
      location={location}
      onLocationChange={onLocationChange}
      leavesParentEmpty={leavesParentEmpty}
      collisionError={collision}
      changeDisabled={movingScope || movingGroup || unchanged}
      onConfirm={confirm}
      onCancel={closeModal}
    />
  );
};

export default MoveScopeModal;
